/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { InsightsL3Nav } from 'src/app/graph/operationsPage/insightsL3Nav/InsightsL3Nav';
import { Loading } from 'src/components/common/loading/Loading';
import { ResizerStyles } from 'src/components/common/resizerStyles/ResizerStyles';
import { SplitPane } from 'src/components/common/splitPane/SplitPane';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { operationTypeFromSignature } from 'src/lib/metrics';

import { insightsRouteConfig } from '../routes';

import { useInsightsL3NavState } from './insightsL3Nav/useInsightsL3NavState';
import { OperationsMainContent } from './OperationsMainContent';

// This excludes lastReportedAt for performance reasons
export const operationsPageQuery = gql<
  GraphQLTypes.OperationsPageQuery,
  GraphQLTypes.OperationsPageQueryVariables
>`
  query OperationsPageQuery(
    $graphId: ID!
    $graphVariant: String!
    $isOperationLandingPage: Boolean!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      id
      graphType
      variant(name: $graphVariant) {
        id
        hasSupergraphSchema
        url
        requestsInLastDay
      }
      account {
        id
      }
      operation(id: $operationId) @include(if: $isOperationLandingPage) {
        id
        name
        signature
      }
    }
  }
`;

// This query includes lastReportedAt and will be refetched for variants that
// have not yet reported any metrics
export const operationsPageHeavyQuery = gql<
  GraphQLTypes.OperationsPageHeavyQuery,
  GraphQLTypes.OperationsPageHeavyQueryVariables
>`
  query OperationsPageHeavyQuery(
    $graphId: ID!
    $graphVariant: String!
    $isOperationLandingPage: Boolean!
    $operationId: ID!
  ) {
    service(id: $graphId) {
      id
      graphType
      lastReportedAt(graphVariant: $graphVariant)
      variant(name: $graphVariant) {
        id
        hasSupergraphSchema
        url
      }
      account {
        id
      }
      operation(id: $operationId) @include(if: $isOperationLandingPage) {
        id
        name
        signature
      }
    }
  }
`;

export const OperationsPage = () => {
  const { isOpen, width, setWidth } = useInsightsL3NavState();
  const isInBackground = useIsAppInBackgroundContext();

  const params = useRouteParams(insightsRouteConfig);
  const {
    graphId,
    queryName,
    query: selectedOperationId,
    graphVariant = Config.service.defaultVariant,
    insightsTab,
  } = params;
  const isOperationLandingPage = Boolean(selectedOperationId);

  const history = useHistory();
  const location = useLocation();
  const {
    data,
    loading: opsPageQueryLoading,
    error,
    networkStatus,
  } = useQuery(operationsPageQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      graphId,
      graphVariant,
      isOperationLandingPage,
      operationId: selectedOperationId || '',
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    // onCompleted is not called for cache writes after Apollo Client 3.8, so
    // don't add logic here that require changes to data.
    onCompleted(d) {
      // TODO: Add displayName to Service.operation. This is the same logic as
      // GraphVariantOperationInsightsListItemConnection.nodes
      // https://github.com/mdg-private/monorepo/blob/9bdb3ce5d8b6ccec925d4d231e624dd7b4924d8e/mdg/insights/src/main/kotlin/mdg/insights/OperationInsightsListRecord.kt#L58-L62
      const newQueryName =
        d?.service?.operation?.name ||
        d?.service?.operation?.signature?.slice(0, 100);

      // Enrich the URL with the queryName if not already present
      if (isOperationLandingPage && !queryName && newQueryName) {
        history.replace(
          insightsRouteConfig.locationFrom({
            location,
            patch: {
              queryName: newQueryName,
            },
          }),
        );
      }
    },
  });

  let reportingEnabled = (data?.service?.variant?.requestsInLastDay || 0) > 0;

  // Poll the heavy query but only if we haven't detected any reporting and the
  // page is in the foreground. This query is expensive so we only want to do it
  // if the faster requestsInLastDay field returned 0.
  const skip = opsPageQueryLoading || isInBackground || reportingEnabled;
  const { data: opsPageQueryHeavyData, loading: opsPageQueryHeavyLoading } =
    useQuery(operationsPageHeavyQuery, {
      skip,
      skipPollAttempt: useCallback(() => skip, [skip]),
      pollInterval: 10000,
      variables: {
        graphId,
        graphVariant,
        isOperationLandingPage,
        operationId: selectedOperationId || '',
      },
    });

  if (!reportingEnabled) {
    reportingEnabled = !!opsPageQueryHeavyData?.service?.lastReportedAt;
  }

  const loading = opsPageQueryLoading || opsPageQueryHeavyLoading;

  const signature = data?.service?.operation?.signature;
  const operationType = signature
    ? operationTypeFromSignature(signature)
    : undefined;

  const selectedOperation = selectedOperationId
    ? {
        id: selectedOperationId,
        type: operationType,
        name: queryName ?? undefined,
      }
    : undefined;

  const trackCustomEvent = useTrackCustomEvent();
  useEffect(() => {
    if (!isOperationLandingPage || loading || !operationType) {
      return;
    }
    trackCustomEvent({
      action: 'landed_on_olp',
      category: 'Operation Insights',
      label: `Operation type: ${operationType.toLowerCase()}`,
    });
  }, [isOperationLandingPage, loading, operationType, trackCustomEvent]);

  useTrackL1PageView('operationsL1', {
    graphRef: {
      graphId,
      graphVariant,
    },
  });

  // This redirect would make more sense on a hypothetical "InsightsPage"
  // component that is a parent of both the Operations and Fields pages.
  // Alas this is what we have today.
  if (!insightsTab) {
    return (
      <Redirect
        to={insightsRouteConfig.locationFrom({
          location,
          patch: { insightsTab: 'operations' },
        })}
      />
    );
  }

  // TODO: Remove this redirect once "fields" has been removed from possible
  // insightsTab values
  if (insightsTab === 'fields') {
    return (
      <Redirect
        to={insightsRouteConfig.locationFrom({
          location,
          patch: {
            insightsTab: 'objectFields',
          },
        })}
      />
    );
  }

  // TODO: When we upgrade to apollo-client 3.8+ we can push our query down into
  // OperationsMainContent, update it to useSuspenseQuery, and handle the loading
  // state here in its parent
  if (error) console.warn(error); // eslint-disable-line no-console

  // Should only show loader on initial load, not subsequent fetches.
  if (loading && networkStatus === NetworkStatus.loading) {
    return <Loading />;
  }

  const opsPageData = opsPageQueryHeavyData || data;
  const opSignature = opsPageData?.service?.operation?.signature || undefined;

  return !isOpen ? (
    <OperationsMainContent
      serviceId={opsPageData?.service?.id}
      accountId={opsPageData?.service?.account?.id}
      operationSignature={opSignature}
      selectedOperation={selectedOperation}
      reportingEnabled={reportingEnabled}
    />
  ) : (
    <div className="relative flex flex-1 flex-col">
      <ResizerStyles />
      <SplitPane
        defaultSize={width}
        css={css`
          & > .Resizer.vertical {
            padding-left: 10px;
            padding-right: 10px;
            border-right: 1px solid ${customColors.border.primary};
            background-color: ${customColors.bg.primary};
          }
          & > .Pane2 {
            overflow-y: auto;
            background-color: ${customColors.bg.primary};
          }
          & > .Pane1 {
            background-color: ${customColors.bg.primary};
          }
        `}
        onDragFinished={setWidth}
      >
        <InsightsL3Nav selectedOperation={selectedOperation} />
        <OperationsMainContent
          serviceId={opsPageData?.service?.id}
          accountId={opsPageData?.service?.account?.id}
          operationSignature={opSignature}
          selectedOperation={selectedOperation}
          reportingEnabled={reportingEnabled}
        />
      </SplitPane>
    </div>
  );
};
